import React, { useEffect, useState } from 'react';
import { Loader2, Ticket, Plus, Save, Trash2, X, Percent, DollarSign } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { adminAPI } from '@/utils/adminApi';
import { formatCOP } from '@/utils/format';
import { CouponStats, KIND_LABELS } from '@/components/admin/CouponStats';

const KIND_FILTERS = [['all', 'Todos'], ['manual', 'Manuales'], ['welcome', 'Bienvenida'], ['referral', 'Referidos'], ['next_purchase', 'Regalo 5%'], ['birthday', 'Cumpleaños']];

const empty = {
  code: '',
  discount_type: 'percentage',
  discount_value: 10,
  min_purchase: 0,
  max_uses: '',
  expiry_date: '',
  is_active: true,
  description: '',
};

const CouponForm = ({ initial, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(initial || empty);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = () => {
    const payload = { ...form };
    if (payload.max_uses === '' || payload.max_uses === null) payload.max_uses = null;
    else payload.max_uses = parseInt(payload.max_uses, 10);
    if (!payload.expiry_date) payload.expiry_date = null;
    else payload.expiry_date = new Date(payload.expiry_date).toISOString();
    onSave(payload);
  };

  return (
    <div className="bg-white rounded-2xl border border-[#2A5C43] p-6 mb-6" data-testid="coupon-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold block mb-1">Código *</span>
          <input value={form.code} onChange={(e) => set('code', e.target.value.toUpperCase())}
            placeholder="BIENVENIDA10"
            className="w-full px-4 py-2.5 rounded-xl border border-[#E7E5E4] text-sm focus:outline-none focus:border-[#2A5C43] uppercase"
            data-testid="coupon-code" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold block mb-1">Tipo *</span>
          <select value={form.discount_type} onChange={(e) => set('discount_type', e.target.value)}
            className="w-full px-4 py-2.5 rounded-xl border border-[#E7E5E4] text-sm focus:outline-none focus:border-[#2A5C43]"
            data-testid="coupon-type">
            <option value="percentage">Porcentaje (%)</option>
            <option value="fixed">Monto fijo (COP)</option>
          </select>
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold block mb-1">Valor *</span>
          <input type="number" value={form.discount_value} onChange={(e) => set('discount_value', parseFloat(e.target.value) || 0)}
            className="w-full px-4 py-2.5 rounded-xl border border-[#E7E5E4] text-sm focus:outline-none focus:border-[#2A5C43]"
            data-testid="coupon-value" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold block mb-1">Compra mínima (COP)</span>
          <input type="number" value={form.min_purchase} onChange={(e) => set('min_purchase', parseFloat(e.target.value) || 0)}
            className="w-full px-4 py-2.5 rounded-xl border border-[#E7E5E4] text-sm focus:outline-none focus:border-[#2A5C43]"
            data-testid="coupon-min" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold block mb-1">Máximo de usos</span>
          <input type="number" value={form.max_uses ?? ''} onChange={(e) => set('max_uses', e.target.value)}
            placeholder="Ilimitado"
            className="w-full px-4 py-2.5 rounded-xl border border-[#E7E5E4] text-sm focus:outline-none focus:border-[#2A5C43]"
            data-testid="coupon-maxuses" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold block mb-1">Vence el</span>
          <input type="date" value={form.expiry_date ? form.expiry_date.slice(0,10) : ''}
            onChange={(e) => set('expiry_date', e.target.value)}
            className="w-full px-4 py-2.5 rounded-xl border border-[#E7E5E4] text-sm focus:outline-none focus:border-[#2A5C43]"
            data-testid="coupon-expiry" />
        </label>
        <label className="md:col-span-2 block">
          <span className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold block mb-1">Descripción interna</span>
          <input value={form.description || ''} onChange={(e) => set('description', e.target.value)}
            placeholder="Ej. Campaña primera compra septiembre"
            className="w-full px-4 py-2.5 rounded-xl border border-[#E7E5E4] text-sm focus:outline-none focus:border-[#2A5C43]"
            data-testid="coupon-desc" />
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer md:col-span-2">
          <input type="checkbox" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)}
            data-testid="coupon-active" />
          Activo
        </label>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button onClick={onCancel} variant="outline" className="rounded-full">
          <X className="w-4 h-4 mr-2" /> Cancelar
        </Button>
        <Button onClick={submit} disabled={saving || !form.code} className="bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full px-6" data-testid="coupon-save">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Guardar
        </Button>
      </div>
    </div>
  );
};

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [kindFilter, setKindFilter] = useState('all');
  const visible = kindFilter === 'all' ? coupons : coupons.filter((c) => c.kind === kindFilter);

  const fetch = async () => {
    setLoading(true);
    try {
      const data = await adminAPI.listCoupons();
      setCoupons(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetch(); }, []);

  const save = async (payload) => {
    setSaving(true);
    try {
      if (editing) await adminAPI.updateCoupon(editing.id, payload);
      else await adminAPI.createCoupon(payload);
      setShowForm(false);
      setEditing(null);
      await fetch();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar este cupón?')) return;
    await adminAPI.deleteCoupon(id);
    await fetch();
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-2">Panel administrativo</p>
            <h1 className="font-display font-bold text-3xl sm:text-4xl text-[#1C1917] tracking-tight" data-testid="admin-coupons-title">Cupones</h1>
            <p className="text-[#57534E] mt-2">Códigos promocionales que los clientes pueden aplicar en el checkout</p>
          </div>
          {!showForm && (
            <Button onClick={() => { setEditing(null); setShowForm(true); }}
              className="bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full px-5" data-testid="add-coupon">
              <Plus className="w-4 h-4 mr-2" /> Nuevo cupón
            </Button>
          )}
        </div>

        <CouponStats />

        {showForm && <CouponForm initial={editing} saving={saving} onSave={save} onCancel={() => { setShowForm(false); setEditing(null); }} />}

        <div className="flex flex-wrap gap-2 mb-5" data-testid="coupon-kind-filters">
          {KIND_FILTERS.map(([k, label]) => {
            const n = k === 'all' ? coupons.length : coupons.filter((c) => c.kind === k).length;
            return (
              <button key={k} onClick={() => setKindFilter(k)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${kindFilter === k ? 'bg-[#2A5C43] text-white border-[#2A5C43]' : 'bg-white text-[#57534E] border-[#E7E5E4] hover:border-[#2A5C43]'}`}
                data-testid={`coupon-filter-${k}`}>
                {label} <span className="opacity-70">({n})</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#2A5C43]" /></div>
        ) : visible.length === 0 && !showForm ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-[#E7E5E4]" data-testid="coupons-empty">
            <Ticket className="w-12 h-12 text-[#8BA58F] mx-auto mb-4" />
            <p className="text-[#57534E] mb-4">No hay cupones en esta categoría</p>
            {kindFilter === 'manual' && (
              <Button onClick={() => setShowForm(true)} className="bg-[#2A5C43] text-white rounded-full">
                <Plus className="w-4 h-4 mr-2" /> Crear el primero
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="coupons-list">
            {visible.map((c) => (
              <div key={c.id} className={`bg-white rounded-2xl border p-5 ${c.is_active ? 'border-[#E7E5E4]' : 'border-[#F5F5F4] opacity-70'}`} data-testid={`coupon-${c.code}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Ticket className="w-4 h-4 text-[#D47A4A]" />
                      <p className="font-display font-bold text-lg text-[#1C1917] tracking-wide">{c.code}</p>
                      {c.kind !== 'manual' && <span className="px-2 py-0.5 text-[10px] bg-[#F0F5EF] text-[#2A5C43] rounded-full font-semibold">{KIND_LABELS[c.kind]}</span>}
                      {c.status === 'used' && <span className="px-2 py-0.5 text-[10px] bg-[#FFF7EE] text-[#D47A4A] rounded-full font-semibold">Usado</span>}
                      {c.status === 'expired' && <span className="px-2 py-0.5 text-[10px] bg-[#F5F5F4] text-[#57534E] rounded-full font-semibold">Vencido</span>}
                      {!c.is_active && <span className="px-2 py-0.5 text-[10px] bg-[#F5F5F4] text-[#57534E] rounded-full font-semibold">Inactivo</span>}
                    </div>
                    {c.description && <p className="text-xs text-[#57534E]">{c.description}</p>}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditing(c); setShowForm(true); }} className="p-2 hover:bg-[#F7F2E8] rounded-full text-[#2A5C43]" data-testid={`edit-coupon-${c.code}`}>
                      <Save className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(c.id)} className="p-2 hover:bg-red-50 rounded-full text-red-600" data-testid={`delete-coupon-${c.code}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-[#8BA58F] uppercase tracking-widest font-semibold mb-0.5">Descuento</p>
                    <p className="font-display font-bold text-[#2A5C43] flex items-center gap-1">
                      {c.discount_type === 'percentage'
                        ? <>{c.discount_value}<Percent className="w-4 h-4" /></>
                        : <><DollarSign className="w-4 h-4" />{formatCOP(c.discount_value)}</>}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-[#8BA58F] uppercase tracking-widest font-semibold mb-0.5">Compra mínima</p>
                    <p className="text-[#1C1917]">{c.min_purchase > 0 ? formatCOP(c.min_purchase) : 'Sin mínimo'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#8BA58F] uppercase tracking-widest font-semibold mb-0.5">Usos</p>
                    <p className="text-[#1C1917]">{c.uses_count || 0}{c.max_uses ? ` / ${c.max_uses}` : ''}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#8BA58F] uppercase tracking-widest font-semibold mb-0.5">Vence</p>
                    <p className="text-[#1C1917]">{c.expiry_date ? new Date(c.expiry_date).toLocaleDateString('es-CO') : 'No expira'}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCoupons;
